/* oxlint-disable typescript/no-deprecated */

import type {
  ZodArray,
  ZodObject,
  ZodOptional,
  ZodRawShape,
  ZodReadonly,
  ZodType,
  core,
} from "zod";
import { z } from "zod";

import type { FieldDescriptionsArgs } from "./field-descriptions.ts";
import { FIELD_DESCRIPTIONS, describeShape } from "./field-descriptions.ts";
import type { ReadonlyRecord, Writable } from "./shared.ts";

const ZodReadonlyProto: Writable<ZodReadonly<ZodObject>> =
  Object.getPrototypeOf(z.object({}).readonly());

function getDescriptions(schema: object): ReadonlyRecord<string, string> {
  return FIELD_DESCRIPTIONS.get(schema) ?? {};
}

function selectDescriptions(
  schema: object,
  keys: ReadonlyRecord<string, true>,
  keep: boolean,
): ReadonlyRecord<string, string> {
  return Object.fromEntries(
    Object.entries(getDescriptions(schema)).filter(
      ([key]) => Object.hasOwn(keys, key) === keep,
    ),
  );
}

function withDescriptions<T extends object>(
  schema: T,
  descriptions: ReadonlyRecord<string, string>,
): T {
  FIELD_DESCRIPTIONS.set(schema, descriptions);

  return schema;
}

ZodReadonlyProto.catchall = function catchall<S extends ZodRawShape>(
  this: ZodReadonly<ZodObject>,
  schema: ZodType,
): ZodReadonly<ZodObject<S>> {
  const extended = this.unwrap().catchall(schema).readonly();

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return withDescriptions(
    extended,
    getDescriptions(this),
  ) as unknown as ZodReadonly<ZodObject<S>>;
};

ZodReadonlyProto.extend = function extend<
  S extends ZodRawShape,
  U extends ZodRawShape,
>(
  this: ZodReadonly<ZodObject>,
  shape: U,
  ...[descriptions]: FieldDescriptionsArgs<U>
): ZodReadonly<ZodObject<core.util.Extend<S, core.util.Writeable<U>>>> {
  const addedDescriptions: ReadonlyRecord<string, string> = descriptions ?? {};

  const extended = this.unwrap()
    .extend(describeShape(shape, addedDescriptions))
    .readonly();

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return withDescriptions(extended, {
    ...getDescriptions(this),
    ...addedDescriptions,
  }) as unknown as ZodReadonly<
    ZodObject<core.util.Extend<S, core.util.Writeable<U>>>
  >;
};

ZodReadonlyProto.max = function max<U extends ZodType>(
  this: ZodReadonly<ZodArray>,
  maxLength: number,
): ZodReadonly<ZodArray<U>> {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return this.unwrap().max(maxLength).readonly() as unknown as ZodReadonly<
    ZodArray<U>
  >;
};

ZodReadonlyProto.min = function min<U extends ZodType>(
  this: ZodReadonly<ZodArray>,
  minLength: number,
): ZodReadonly<ZodArray<U>> {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return this.unwrap().min(minLength).readonly() as unknown as ZodReadonly<
    ZodArray<U>
  >;
};

ZodReadonlyProto.nonempty = function nonempty<U extends ZodType>(
  this: ZodReadonly<ZodArray>,
): ZodReadonly<ZodArray<U>> {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return this.unwrap().nonempty().readonly() as unknown as ZodReadonly<
    ZodArray<U>
  >;
};

ZodReadonlyProto.omit = function omit<S extends ZodRawShape, K extends keyof S>(
  this: ZodReadonly<ZodObject>,
  keys: ReadonlyRecord<string, true>,
): ZodReadonly<ZodObject<Omit<S, K>>> {
  const omitted = this.unwrap().omit(keys).readonly();

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return withDescriptions(
    omitted,
    selectDescriptions(this, keys, false),
  ) as unknown as ZodReadonly<ZodObject<Omit<S, K>>>;
};

ZodReadonlyProto.partial = function partial<S extends ZodRawShape>(
  this: ZodReadonly<ZodObject>,
): ZodReadonly<ZodObject<{ [K in keyof S]: ZodOptional<S[K]> }>> {
  const optional = this.unwrap().partial().readonly();

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return withDescriptions(
    optional,
    getDescriptions(this),
  ) as unknown as ZodReadonly<ZodObject<{ [K in keyof S]: ZodOptional<S[K]> }>>;
};

ZodReadonlyProto.pick = function pick<S extends ZodRawShape, K extends keyof S>(
  this: ZodReadonly<ZodObject>,
  keys: ReadonlyRecord<string, true>,
): ZodReadonly<ZodObject<Pick<S, K>>> {
  const picked = this.unwrap().pick(keys).readonly();

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return withDescriptions(
    picked,
    selectDescriptions(this, keys, true),
  ) as unknown as ZodReadonly<ZodObject<Pick<S, K>>>;
};

ZodReadonlyProto.strict = function strict<S extends ZodRawShape>(
  this: ZodReadonly<ZodObject>,
): ZodReadonly<ZodObject<S>> {
  const strictObject = this.unwrap().strict().readonly();

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return withDescriptions(
    strictObject,
    getDescriptions(this),
  ) as unknown as ZodReadonly<ZodObject<S>>;
};

Object.defineProperty(ZodReadonlyProto, "descriptions", {
  configurable: true,
  get(this: ZodReadonly<ZodObject>) {
    return getDescriptions(this);
  },
});

Object.defineProperty(ZodReadonlyProto, "shape", {
  configurable: true,
  get(this: ZodReadonly<ZodObject>) {
    const unwrapped = this.unwrap();

    return unwrapped.shape;
  },
});
